/**
 * The cold start: a person signs up through Command and ends up in an organisation
 * Command can actually act for (SYN-2296).
 *
 * 🔴 THE DEFECT THIS EXISTS FOR, MEASURED ON CLOCH 16 Aug 2026 23:52Z.
 *
 * A fresh Clerk user was created via the Backend API, signed in to command.cloch.ai, and estate-literals-allow: prose in an incident record, not a value anything reads
 * landed on `/chat`. Clerk reported **zero organisation memberships**, and it stayed that
 * way: nothing in Command's signed-in path creates an organisation for someone who arrives
 * without one. Every call the app then made came back
 *
 *     403 {"error":"Organization context required"}     (src/auth/middleware.ts)
 *
 * on the database connections list, `/api/conversations` and `/api/me/agents`. The product could
 * not list a connection, could not open a conversation, could not answer anything.
 *
 * ⚠️ THIS CORRECTS THE CANON. The root defect was recorded on 16 Aug 21:15Z as *"Command
 * never calls `POST /api/v1/onboarding/clerk-orgs`, so the user 403s `unknown_user`"* —
 * located in Coherence's JIT provisioning. The measurement says the chain stops one link
 * EARLIER: the user never reaches Coherence at all, because Command's own auth middleware
 * refuses a Clerk JWT that carries no `org_id`. The onboarding call is the second link, and
 * it is unreachable until an organisation exists. Both are real; the order matters, because
 * fixing only the second one changes nothing a customer would notice.
 *
 * WHY THE ORG CREATION AND THE PROVISIONING CALL ARE ONE FUNCTION
 * --------------------------------------------------------------
 * A Clerk organisation on its own buys nothing. It clears Command's middleware and then
 * fails at the next door, because Coherence has no `clerk_orgs` row and JIT provisioning of
 * the `clerk_users` row *raises when the org itself has not been synced*
 * (`gateway/auth_resolver.py:_jit_provision_clerk_user`). So the two steps are a single
 * fact — "this person has a workspace" — and they live in one place rather than two.
 *
 * REUSE, NOT A SECOND PATH (principle 2)
 * --------------------------------------
 * The provisioning call is `claimPaidIntent`, unchanged, with no intent id. It already
 * posts `/api/onboarding/org`, already sends `X-Org-Context` for the window where the JWT
 * has not caught up with `setActive`, already retries a settling upstream, and Atlas's
 * route already treats a claim with no `intentId` as the self-serve case
 * (`if (intentId) {...}` — the paid branch is optional). Writing a second provisioning path
 * for self-serve would have been the same cross-repo duplicate SYN-2471 was filed for.
 *
 * SCOPING — WHAT EVIDENCE DECIDES WHETHER THIS APPLIES (build test 4)
 * ------------------------------------------------------------------
 * It reads one thing: **does this signed-in user have zero organisation memberships?** That
 * is evidence from the environment itself. No hostname, no `if (ENV === …)`, no list.
 *
 * - An environment where users always arrive with an org (the invitation and checkout
 *   flows create one before they land) never enters this branch and is unaffected.
 * - An environment where somebody is *already* broken heals them on their next page load,
 *   which a Clerk webhook could not have done — and Clerk webhooks do not reach this
 *   estate anyway, so a webhook fix would have looked correct and never fired.
 * - It is idempotent by construction: an existing org short-circuits, and the claim
 *   endpoint is idempotent upstream, keyed `atlas-self-serve-org:<orgId>:<userId>`.
 *
 * 🪤 THE FAILURE DIRECTION IS DELIBERATE. If the claim fails, this does NOT return the
 * organisation. A Clerk org with no Coherence tenancy behind it is exactly the half-state
 * that makes the product claim it is connected while every request 403s — the thing the
 * cold run found. Better to report no organisation and let the caller say so than to hand
 * back one that does not work.
 *
 * ⚠️ CORRECTION, 17 Aug 2026 (SYN-2491, found in review before this was wired to anything).
 * The claim above that "somebody already broken heals on their next page load" was FALSE in
 * the one branch that matters. Trace the failure: create the Clerk org, setActive, claim
 * fails, return `{ok:false}` — but the org now EXISTS. On the next load
 * `findExistingOrganization` finds it and short-circuits on the reasoning "an org that
 * exists is an org some path already provisioned", so the claim is never retried. Coherence
 * still has no tenancy, so every request 403s again, for good, and the caller's error state
 * is transient so nothing says why. The half-state this file was written to prevent was
 * reachable through this file.
 *
 * ⇒ `PendingClaimStore` closes it. The mark is written BEFORE the claim is attempted, not
 * after it fails (principle 4: enforce at write time, do not compensate at read time), so
 * the tab that is closed mid-claim is recorded too. An org carrying a mark gets its claim
 * retried on the next load, and the claim is idempotent upstream, so a retry that was not
 * needed costs one request and changes nothing.
 */

import { fetchWithAuth } from "./api";
import { claimPaidIntent } from "./claim-paid-intent";

export interface EnsuredOrganization {
  orgId: string;
  orgName: string;
}

/**
 * An organisation this person could join, and the act that joins it.
 *
 * An offer is never taken automatically. It is rendered, and a human clicks. See the
 * "SOMEONE ELSE'S ORGANISATION" note below for why that is the whole safety property.
 */
export interface OrgJoinOffer {
  /** Clerk's id for the invitation or suggestion, not the organisation. */
  id: string;
  /**
   * `invitation` — accepting makes them a member immediately (Clerk enrollment mode
   * `automatic_invitation`).
   * `suggestion` — accepting raises a membership REQUEST that an admin of that
   * organisation must approve (Clerk enrollment mode `automatic_suggestion`).
   */
  kind: "invitation" | "suggestion";
  orgId: string;
  orgName: string;
  /**
   * The address Clerk says this offer was addressed to, when Clerk says. Invitations carry
   * one; `OrganizationSuggestionResource` does not, which is why the two are gated
   * differently. See `findOrganizationJoinOffers`.
   */
  matchedEmailAddress: string | null;
  accept: () => Promise<void>;
}

/**
 * A join this person has already asked for and that nobody has answered yet.
 *
 * 🔴 THIS IS NOT COSMETIC. Without it, a person who asked to join on Monday gets a personal
 * workspace created behind them on Tuesday, because the suggestion that produced the ask has
 * moved from `pending` to `accepted` and so is no longer an offer. Clerk's
 * `OrganizationSuggestionStatus` is `'pending' | 'accepted'`, where `accepted` means "this
 * user pressed the button", NOT "they are in". Reading `accepted` as "nothing outstanding"
 * silently overrides a choice the person already made.
 */
export interface OrgJoinRequest {
  id: string;
  orgId: string;
  orgName: string;
}

/** Why the server is asking for a business name. Copy only; never a decision input here. */
export type BusinessNameReason =
  | "personal_email"
  | "unverified_email"
  | "no_email"
  | "domain_refused";

export type EnsureSelfServeOrgResult =
  | {
      ok: true;
      kind: "organization";
      organization: EnsuredOrganization;
      created: boolean;
      /**
       * Present only when this call created or joined the org: "admin" for the person who
       * created it, "member" for a colleague who joined their company's workspace.
       */
      role?: "admin" | "member";
    }
  | { ok: true; kind: "choice"; offers: OrgJoinOffer[]; pendingRequests: OrgJoinRequest[] }
  /**
   * A personal or unverified address: nothing was created. Ask the person what their business
   * is called and call again with `businessName`. `rejectedName` is set when a name WAS sent
   * and the server could not use it (empty after tidying, or too long).
   */
  | {
      ok: true;
      kind: "needs_business_name";
      reason: BusinessNameReason;
      rejectedName: string | null;
    }
  /**
   * Purchase only: a personal-address buyer who owns workspaces is asked which one this is for,
   * or "a new one" (Maria, 5 Oct 2026). Call again with `chosenOrgId` or `businessName`.
   */
  | { ok: true; kind: "needs_workspace_choice"; workspaces: WorkspaceOption[] }
  /**
   * `retryable: false` only when the server said no retry can fix it (Clerk refused to create
   * the org, or the workspace already holds another purchase). Absent means "may retry".
   */
  | { ok: false; kind: "error"; reason: string; retryable?: false };

/** A workspace offered for a purchase: one the buyer is an admin of. */
export interface WorkspaceOption {
  orgId: string;
  orgName: string;
}

/** The server's answer to `POST /api/onboarding/workspace`. */
export type WorkspaceRequestResult =
  | {
      ok: true;
      kind: "organization";
      outcome: "existing" | "created" | "joined";
      orgId: string;
      orgName: string;
      role: "admin" | "member";
    }
  | { ok: true; kind: "needs_business_name"; reason: BusinessNameReason }
  | { ok: true; kind: "needs_workspace_choice"; workspaces: WorkspaceOption[] }
  | { ok: false; error: string; retryable?: false };

const BUSINESS_NAME_REASONS: readonly BusinessNameReason[] = [
  "personal_email",
  "unverified_email",
  "no_email",
  "domain_refused",
];

/** Ask Command's server for this person's workspace. See `src/onboarding/workspace-by-domain.ts`. */
export async function requestWorkspaceFromCommand(
  input: {
    businessName: string | null;
    purpose?: "purchase";
    purchaseIntentId?: string | null;
    chosenOrgId?: string | null;
  },
  fetcher: typeof fetchWithAuth = fetchWithAuth,
): Promise<WorkspaceRequestResult> {
  const response = await fetcher("/api/onboarding/workspace", {
    method: "POST",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify({
      ...(input.businessName ? { businessName: input.businessName } : {}),
      ...(input.purpose ? { purpose: input.purpose } : {}),
      ...(input.purchaseIntentId ? { purchaseIntentId: input.purchaseIntentId } : {}),
      ...(input.chosenOrgId ? { chosenOrgId: input.chosenOrgId } : {}),
    }),
  });
  let body: Record<string, unknown> = {};
  try {
    const parsed: unknown = await response.json();
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      body = parsed as Record<string, unknown>;
    }
  } catch {
    // Fall through to the status-based error below.
  }
  if (!response.ok) {
    const error = typeof body.error === "string" && body.error ? body.error : "";
    const failed = `workspace_setup_failed: ${error || response.status}`;
    // Only the server's explicit `false` stops retries; anything else stays retryable.
    return body.retryable === false
      ? { ok: false, error: failed, retryable: false }
      : { ok: false, error: failed };
  }
  if (body.kind === "needs_business_name") {
    const reason = BUSINESS_NAME_REASONS.find((r) => r === body.reason) ?? "no_email";
    return { ok: true, kind: "needs_business_name", reason };
  }
  if (body.kind === "needs_workspace_choice" && Array.isArray(body.workspaces)) {
    const workspaces = body.workspaces.flatMap((w: unknown) => {
      const o = w as { orgId?: unknown; orgName?: unknown } | null;
      return o && typeof o.orgId === "string" && isOrganizationId(o.orgId)
        ? [{ orgId: o.orgId, orgName: typeof o.orgName === "string" ? o.orgName : "" }]
        : [];
    });
    return { ok: true, kind: "needs_workspace_choice", workspaces };
  }
  if (
    body.kind === "organization" &&
    typeof body.orgId === "string" &&
    (body.outcome === "existing" || body.outcome === "created" || body.outcome === "joined")
  ) {
    return {
      ok: true,
      kind: "organization",
      outcome: body.outcome,
      orgId: body.orgId,
      orgName: typeof body.orgName === "string" ? body.orgName : "",
      role: body.role === "admin" ? "admin" : "member",
    };
  }
  return { ok: false, error: "workspace_setup_failed: unrecognised_response" };
}

interface OrgResource {
  id?: string | null;
  name?: string | null;
}

interface MembershipResource {
  organization?: OrgResource | null;
}

interface PublicOrganizationDataResource {
  id?: string | null;
  name?: string | null;
}

/** Clerk's `UserOrganizationInvitationResource`, narrowed to what is read here. */
interface OrgInvitationResource {
  id?: string | null;
  emailAddress?: string | null;
  status?: string | null;
  publicOrganizationData?: PublicOrganizationDataResource | null;
  accept?: () => Promise<unknown>;
}

/** Clerk's `OrganizationSuggestionResource`, narrowed to what is read here. */
interface OrgSuggestionResource {
  id?: string | null;
  status?: string | null;
  publicOrganizationData?: PublicOrganizationDataResource | null;
  accept?: () => Promise<unknown>;
}

/** The narrow slice of Clerk this needs. Kept structural so tests need no Clerk. */
export interface ClerkOrgEnsureClient {
  organization?: OrgResource | null;
  createOrganization?: (params: { name: string }) => Promise<OrgResource | null>;
  setActive?: (params: { organization: string }) => Promise<unknown>;
  session?: {
    getToken?: (options?: { template?: string; skipCache?: boolean }) => Promise<string | null>;
  };
  user?: {
    getOrganizationMemberships?: (
      params?: unknown,
    ) => Promise<{ data?: MembershipResource[] | null } | null>;
    getOrganizationInvitations?: (
      params?: unknown,
    ) => Promise<{ data?: OrgInvitationResource[] | null } | null>;
    getOrganizationSuggestions?: (
      params?: unknown,
    ) => Promise<{ data?: OrgSuggestionResource[] | null } | null>;
  } | null;
}

/**
 * One email address on the account, with Clerk's own verdict on whether it is verified.
 *
 * `verification.status` is Clerk's `VerificationStatus`:
 * `'unverified' | 'verified' | 'transferable' | 'failed' | 'expired'`. Only the exact string
 * `'verified'` counts here. `'transferable'` in particular is NOT verified — it is Clerk
 * saying an identifier could be moved onto an SSO account, which is a claim about a possible
 * future, not about this person having proved they read that mailbox.
 */
export interface EnsureSelfServeOrgEmailAddress {
  emailAddress?: string | null;
  verification?: { status?: string | null } | null;
}

export interface EnsureSelfServeOrgUser {
  firstName?: string | null;
  lastName?: string | null;
  fullName?: string | null;
  primaryEmailAddress?: EnsureSelfServeOrgEmailAddress | null;
  /**
   * EVERY address on the account, not just the primary.
   *
   * The suggestion gate below needs to prove that no unverified address exists, and it
   * cannot prove that from the primary alone. An absent or empty array therefore reads as
   * "we cannot enumerate this account", which fails closed to no offer.
   */
  emailAddresses?: EnsureSelfServeOrgEmailAddress[] | null;
}

export function isOrganizationId(id: string | null | undefined): id is string {
  return typeof id === "string" && id.startsWith("org_");
}

/**
 * Find an organisation the user already has, before creating one.
 *
 * Two reads, because they disagree at exactly the wrong moment: `clerk.organization` is the
 * active org and is set immediately on some sign-up paths, while the membership list is
 * authoritative but can lag a `setActive` by a beat. Checking only the first creates a
 * duplicate org for an invited buyer; checking only the second creates one for a user whose
 * Clerk instance auto-created theirs.
 */
export async function findExistingOrganization(
  clerk: ClerkOrgEnsureClient,
): Promise<EnsuredOrganization | null> {
  if (isOrganizationId(clerk.organization?.id)) {
    return {
      orgId: clerk.organization.id,
      orgName: clerk.organization?.name ?? "",
    };
  }
  const memberships = await clerk.user?.getOrganizationMemberships?.().catch(() => null);
  const match = memberships?.data?.find((m) => isOrganizationId(m.organization?.id));
  if (match?.organization?.id && isOrganizationId(match.organization.id)) {
    return { orgId: match.organization.id, orgName: match.organization.name ?? "" };
  }
  return null;
}

/* ------------------------------------------------------------------------------------- *
 * SOMEONE ELSE'S ORGANISATION
 * ------------------------------------------------------------------------------------- *
 *
 * The rule, in Maria's words, 4 Sep 2026:
 *
 *     "they get their own org if one doesn't already exist … or they get a choice if one
 *      does based on their work domain (excluding gmail, hotmail, etc)"
 *
 * 🔴 THE FAILURE MODE HERE IS WORSE THAN THE DEFECT IT FIXES. Putting the wrong person into
 * an organisation hands them another tenant's connections, credentials and data. Every
 * decision below is therefore biased toward ISOLATION: where the code cannot establish
 * something, the answer is their own workspace, which is at worst a nuisance.
 *
 * THREE QUESTIONS, AND WHO OWNS EACH ANSWER (principle 2)
 * ------------------------------------------------------
 *
 * 1. "Is this a work domain or a consumer one?"  — CLERK OWNS IT, WE DO NOT ASK.
 *    We keep no list of gmail/hotmail/yahoo, because a list we maintain is a fact that goes
 *    stale silently and is wrong in the dangerous direction the day it does. Clerk refuses
 *    to let an organisation verify a consumer or disposable domain at all: "A Verified
 *    Domain cannot be a disposable domain or common email provider. For example, you cannot
 *    create a Verified Domain for @gmail.com." So a gmail address can never produce an
 *    offer, because no organisation can ever hold @gmail.com as a verified domain. The list
 *    is maintained by Clerk, updated without a deploy, and we never see it.
 *
 * 2. "Does an organisation already exist for that domain?" — CLERK OWNS IT.
 *    Clerk's Verified Domains are the first-class mechanism and we use it rather than
 *    inventing one. There is NO domain column on `clerk_orgs` (id, name, slug, created_at),
 *    so the alternative would have been matching an org NAME or SLUG against the domain —
 *    a substring standing in for a fact, and the exact shape of mistake that has cost this
 *    estate whole days. We never look at a name or a slug. We ask Clerk what it is offering
 *    this user and we render that.
 *
 * 3. "Is the email actually theirs?" — WE CHECK IT OURSELVES, BECAUSE CLERK DOES NOT SAY.
 *    ⚠️ Clerk's verified-domains documentation states only that a user "will receive an
 *    invitation … if their email's domain matches the Verified Domain". It does NOT state
 *    that the matched address must be verified, and no page found says so. An unflagged gap
 *    reads as a verified fact, so this code does not rely on the guarantee. It reads Clerk's
 *    own per-address `verification.status` off the user and requires the exact string
 *    `'verified'`.
 *
 * WHY INVITATIONS AND SUGGESTIONS ARE GATED DIFFERENTLY
 * ----------------------------------------------------
 * `UserOrganizationInvitationResource` carries `emailAddress`, so an invitation can be bound
 * to the exact address it was issued to, and we require THAT address to be verified on this
 * account. `OrganizationSuggestionResource` carries no address, so there is nothing to bind
 * to; we cannot tell which address produced it. The only honest reading is that ANY address
 * on the account could have, so a suggestion is offered only when EVERY address on the
 * account is verified, and only when the account's addresses can be enumerated at all.
 *
 * ⚖️ WHY A CONSUMER ADDRESS IS NOT FILTERED OUT HERE, RAISED IN REVIEW AND REFUTED
 * -------------------------------------------------------------------------------
 * A reviewer read Maria's "excluding gmail, hotmail, etc" as "never show an offer to a
 * consumer address" and called both branches below a breach. It is worth writing down why
 * they are not, because the reading is reasonable and will come round again.
 *
 * The exclusion is about offers DERIVED FROM A DOMAIN, and there are only two ways an offer
 * can exist at all:
 *
 * - An INVITATION can be issued by hand. `organization.inviteMember(emailAddress)` is a named
 *   human at that organisation deciding to invite this exact address. Suppressing it because
 *   the address ends in @gmail.com would break the ordinary invite flow and would hide a
 *   decision a person actually took. Nothing was inferred from the domain, so the exclusion
 *   does not apply. It is bound to a verified address either way.
 *
 * - A SUGGESTION has no manual form. It exists only because an organisation holds a VERIFIED
 *   DOMAIN in `automatic_suggestion` mode, and Clerk will not verify a consumer or disposable
 *   domain. So the exclusion is already enforced, upstream, by the party that maintains the
 *   list.
 *
 * ⚠️ THE RESIDUAL RISK, STATED RATHER THAN HIDDEN. Clerk's list of consumer domains is a
 * maintained fact and it WILL be wrong about something — a regional free-mail provider it has
 * not classified. On that domain an organisation could verify and then suggest to strangers.
 * Two things bound it, and neither is this file: the person must press "Ask to join", and an
 * admin of that organisation must then approve a membership request. A wrong entry in
 * somebody else's list cannot move anybody on its own. We do not mirror the list to close
 * that gap, because a copy we maintain goes stale silently and fails in the same direction
 * with nobody watching (principle 2).
 *
 * ⚖️ "A CLIENT-SIDE CHECK IS NOT AN IDENTITY PROOF", RAISED IN REVIEW AND REFUTED
 * ------------------------------------------------------------------------------
 * True, and it is not what this is. Everything here runs in the browser, so a tampered client
 * could call `invitation.accept()` directly from a console and never reach this code at all.
 * This is not the boundary and cannot be.
 *
 * What it is: a NARROWING filter over what Clerk already decided to offer. It can only ever
 * make the offered set SMALLER than Clerk's, never larger, so it cannot grant anything. The
 * authority to join is Clerk's, enforced server-side on `accept()`, and Command never mints a
 * membership itself. The reason the filter exists anyway is that Clerk does not document a
 * verification guarantee on the match, and a product that shows somebody a "Join Acme" button
 * on the strength of an address they typed and never confirmed has already done the damage,
 * whatever the server later decides.
 *
 * ⚠️ SUPERSEDED IN PART, 5 Oct 2026 (Maria): "same company joins the same workspace". A person
 * whose VERIFIED primary address is at a company domain that a workspace already holds as a
 * Clerk Verified Domain is now added to it automatically, as a member, by Command's server
 * (`src/onboarding/workspace-by-domain.ts`). That is a different path from the offers below:
 * it runs only when there is NO offer, is decided server-side from Clerk's own verification
 * status, and never applies to a personal-provider or unverified address. The offers below
 * (manual invitations, and suggestions from domains registered in suggestion mode) are
 * unchanged and are still a human's choice.
 *
 * WHAT THIS DELIBERATELY DOES NOT DO
 * ----------------------------------
 * - It never accepts an offer. It returns offers; a human clicks.
 * - It never runs for a user who already has an organisation. `findExistingOrganization`
 *   short-circuits first, so no existing account is moved and no live credential is
 *   orphaned. Migration is a separate decision and is not taken here.
 * - It never fails the sign-in. Any error listing offers reads as "no offers", which lands
 *   on the server's workspace decision below (company workspace, or ask for a name).
 */

/** Clerk's `VerificationStatus` value that means "this person proved they read that inbox". */
const VERIFIED = "verified";

function normaliseAddress(value: string | null | undefined): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim().toLowerCase();
  return trimmed.length > 0 ? trimmed : null;
}

function isVerified(address: EnsureSelfServeOrgEmailAddress | null | undefined): boolean {
  return address?.verification?.status === VERIFIED;
}

/**
 * Every address on this account that Clerk says is verified, lowercased.
 *
 * The primary is folded in as well as the array, because a caller holding only the primary
 * still gets a correct (smaller) answer rather than an empty one.
 */
export function verifiedEmailAddresses(user: EnsureSelfServeOrgUser | null): string[] {
  const found = new Set<string>();
  const consider = (entry: EnsureSelfServeOrgEmailAddress | null | undefined): void => {
    if (!isVerified(entry)) return;
    const address = normaliseAddress(entry?.emailAddress);
    if (address) found.add(address);
  };
  consider(user?.primaryEmailAddress);
  for (const entry of user?.emailAddresses ?? []) consider(entry);
  return [...found];
}

/**
 * Can we prove that NO unverified address hangs off this account?
 *
 * Only `emailAddresses` can answer that, and only when it is actually populated. Absent,
 * empty, or not an array all mean "we cannot enumerate this account", and an unprovable
 * claim resolves to no, not to yes.
 */
export function hasOnlyVerifiedEmailAddresses(user: EnsureSelfServeOrgUser | null): boolean {
  const addresses = user?.emailAddresses;
  if (!Array.isArray(addresses) || addresses.length === 0) return false;
  if (!addresses.every((entry) => isVerified(entry))) return false;
  // The primary must be verified too, and must not be some address the array never listed.
  const primary = user?.primaryEmailAddress;
  if (primary && !isVerified(primary)) return false;
  return true;
}

function organisationOf(
  data: PublicOrganizationDataResource | null | undefined,
): EnsuredOrganization | null {
  const orgId = data?.id;
  if (!isOrganizationId(orgId)) return null;
  const orgName = typeof data?.name === "string" ? data.name.trim() : "";
  return { orgId, orgName };
}

/**
 * Ask Clerk what it is offering this user, and drop everything we cannot stand behind.
 *
 * Returns offers (a human may take them) and pendingRequests (a human already did, and is
 * waiting on somebody else). Both empty is the overwhelmingly common answer and means
 * "carry on and make them their own workspace".
 */
export async function findOrganizationJoinOffers(
  clerk: ClerkOrgEnsureClient,
  user: EnsureSelfServeOrgUser | null,
): Promise<{ offers: OrgJoinOffer[]; pendingRequests: OrgJoinRequest[] }> {
  const empty = { offers: [], pendingRequests: [] };
  const verified = new Set(verifiedEmailAddresses(user));
  // No verified address at all: nothing here can be trusted, and there is nothing to bind
  // an invitation to. Fail closed before a single call is made.
  if (verified.size === 0) return empty;

  const [invitationPage, suggestionPage] = await Promise.all([
    Promise.resolve()
      .then(() => clerk.user?.getOrganizationInvitations?.({ status: "pending" }))
      .catch(() => null),
    Promise.resolve()
      .then(() => clerk.user?.getOrganizationSuggestions?.({ status: ["pending", "accepted"] }))
      .catch(() => null),
  ]);

  const offers: OrgJoinOffer[] = [];
  const pendingRequests: OrgJoinRequest[] = [];
  const claimedOrgIds = new Set<string>();

  // Invitations first: accepting one is a single click into membership, and where an org
  // offers both we want the stronger, better-bound of the two.
  for (const invitation of invitationPage?.data ?? []) {
    // Re-filter the status. The parameter above is a request, not a promise, and an offer
    // that is revoked or expired must not be rendered as live.
    if (invitation?.status !== "pending") continue;
    const id = normaliseAddress(invitation?.id) ? String(invitation.id) : null;
    if (!id) continue;
    const organisation = organisationOf(invitation?.publicOrganizationData);
    if (!organisation) continue;
    const accept = invitation?.accept;
    // An offer nobody can act on is not a choice. Do not render a dead button.
    if (typeof accept !== "function") continue;
    // 🔑 THE BINDING. This invitation names an address; that address must be one this
    // account has proved. Anything else is a claim from outside that we would be trusting.
    const addressed = normaliseAddress(invitation?.emailAddress);
    if (!addressed || !verified.has(addressed)) continue;
    if (claimedOrgIds.has(organisation.orgId)) continue;
    claimedOrgIds.add(organisation.orgId);
    offers.push({
      id,
      kind: "invitation",
      orgId: organisation.orgId,
      orgName: organisation.orgName,
      matchedEmailAddress: addressed,
      accept: async () => {
        await accept.call(invitation);
      },
    });
  }

  // Suggestions carry no address, so they need the whole account to be clean.
  const suggestionsAllowed = hasOnlyVerifiedEmailAddresses(user);
  for (const suggestion of suggestionPage?.data ?? []) {
    const id = normaliseAddress(suggestion?.id) ? String(suggestion.id) : null;
    if (!id) continue;
    const organisation = organisationOf(suggestion?.publicOrganizationData);
    if (!organisation) continue;

    if (suggestion?.status === "accepted") {
      // 🔴 NOT AN OFFER — A REQUEST ALREADY MADE. This is reported even when the gate below
      // would refuse a fresh offer, because it records something the PERSON did. Suppressing
      // it does not protect anybody; it just lets a workspace be created over the top of a
      // decision they already took.
      if (claimedOrgIds.has(organisation.orgId)) continue;
      claimedOrgIds.add(organisation.orgId);
      pendingRequests.push({ id, orgId: organisation.orgId, orgName: organisation.orgName });
      continue;
    }

    if (suggestion?.status !== "pending") continue;
    if (!suggestionsAllowed) continue;
    const accept = suggestion?.accept;
    if (typeof accept !== "function") continue;
    if (claimedOrgIds.has(organisation.orgId)) continue;
    claimedOrgIds.add(organisation.orgId);
    offers.push({
      id,
      kind: "suggestion",
      orgId: organisation.orgId,
      orgName: organisation.orgName,
      matchedEmailAddress: null,
      accept: async () => {
        await accept.call(suggestion);
      },
    });
  }

  return { offers, pendingRequests };
}

/**
 * The client's own record of a claim it STARTED and did not see succeed.
 *
 * 🔑 This is not a second copy of a fact Coherence owns (principle 2). Coherence owns
 * "is this org provisioned"; this store holds "we fired a claim for this org and never
 * saw it return ok", which is a fact about our own action and nowhere else. It is a
 * negative marker only: its absence asserts nothing, so it can never make a broken org
 * look healthy — only a healthy one do one extra idempotent request.
 */
export interface PendingClaimStore {
  has(orgId: string): boolean;
  mark(orgId: string): void;
  clear(orgId: string): void;
}

export const PENDING_CLAIM_STORAGE_PREFIX = "command.org-claim-pending:";

/**
 * The default store. localStorage rather than sessionStorage: the user who closes the tab
 * mid-claim is exactly the user this exists for, and a session-scoped mark would forget them.
 * Every access is guarded — a browser with storage disabled degrades to "no mark", which is
 * the pre-existing behaviour rather than an exception on a signed-in render path.
 */
export function localStoragePendingClaims(): PendingClaimStore {
  const key = (orgId: string) => `${PENDING_CLAIM_STORAGE_PREFIX}${orgId}`;
  return {
    has(orgId) {
      try {
        return localStorage.getItem(key(orgId)) !== null;
      } catch {
        return false;
      }
    },
    mark(orgId) {
      try {
        localStorage.setItem(key(orgId), "1");
      } catch {
        // Best effort. See the note above on storage being unavailable.
      }
    },
    clear(orgId) {
      try {
        localStorage.removeItem(key(orgId));
      } catch {
        // Best effort.
      }
    },
  };
}

export interface EnsureSelfServeOrgOptions {
  /** Injection point for tests. Defaults to the shared claim path. */
  claim?: typeof claimPaidIntent;
  /** Injection point for tests. Defaults to the localStorage-backed store. */
  pendingClaims?: PendingClaimStore;
  /**
   * THE EXPLICIT ACT. Set only when a person has looked at the offers and chosen their own
   * workspace anyway. It is the only thing that lets this function create an organisation
   * while an offer or an outstanding request exists.
   *
   * It is deliberately not a "skip the lookup" flag. It is a record that a human already
   * answered the question the lookup asks.
   */
  chooseOwnWorkspace?: boolean;
  /** Injection point for tests. Defaults to the Clerk-backed lookup. */
  findJoinOffers?: typeof findOrganizationJoinOffers;
  /** Injection point for tests. Defaults to `POST /api/onboarding/workspace`. */
  requestWorkspace?: (input: {
    businessName: string | null;
    purpose?: "purchase";
    purchaseIntentId?: string | null;
    chosenOrgId?: string | null;
  }) => Promise<WorkspaceRequestResult>;
  /**
   * The person's answer to "what is your business called". Only ever sent after the server
   * said it needs one (a personal or unverified address); a verified company address ignores
   * it and gets its company's workspace.
   */
  businessName?: string | null;
  /**
   * `"purchase"` (SYN-3371): the workspace a person is PAYING for. The org that happens to be
   * open is not consulted, and neither are offers: an accountant whose active org is a
   * client's would otherwise put her subscription on the client. The server decides from her
   * own verified domain (`purpose` in `src/onboarding/workspace-by-domain.ts`).
   */
  purpose?: "purchase";
  /**
   * With `purpose: "purchase"`: the paid intent, which the server uses as the purchase's key so
   * a retry, refresh or "Try again" finds the workspace it already made.
   */
  purchaseIntentId?: string | null;
  /** With `purpose: "purchase"`: the workspace she picked from `needs_workspace_choice`. */
  chosenOrgId?: string | null;
}

/**
 * Make sure this signed-in user has an organisation Command can act for, and that
 * Coherence and Atlas know about it. Safe to call on every load.
 */
export async function ensureSelfServeOrganization(
  clerk: ClerkOrgEnsureClient,
  user: EnsureSelfServeOrgUser | null,
  options: EnsureSelfServeOrgOptions = {},
): Promise<EnsureSelfServeOrgResult> {
  const claim = options.claim ?? claimPaidIntent;
  const pendingClaims = options.pendingClaims ?? localStoragePendingClaims();
  const findJoinOffers = options.findJoinOffers ?? findOrganizationJoinOffers;

  const forPurchase = options.purpose === "purchase";
  const existing = forPurchase ? null : await findExistingOrganization(clerk);
  if (existing) {
    // Already has one. The claim is idempotent, but re-running it on every page load for
    // every user would put a provisioning call in front of every render, so the ensure
    // normally stops here: an org that exists is an org some path already provisioned.
    //
    // 🔴 UNLESS WE KNOW BETTER. A mark means a claim for this org was started and never
    // seen to succeed, so "some path already provisioned it" is exactly the assumption
    // that is wrong. Retry it, or this user 403s forever. See the header correction.
    if (!pendingClaims.has(existing.orgId)) {
      return { ok: true, kind: "organization", organization: existing, created: false };
    }
    const repair = await claim({ orgId: existing.orgId });
    if (!repair.ok) {
      return { ok: false, kind: "error", reason: `provisioning_failed: ${repair.error}` };
    }
    pendingClaims.clear(existing.orgId);
    return { ok: true, kind: "organization", organization: existing, created: false };
  }

  /*
   * 🔑 THE ONLY NEW BRANCH, AND IT SITS AFTER THE EXISTING-ORG CHECK ON PURPOSE.
   *
   * Everything above returns for anybody who already belongs somewhere, so nobody is ever
   * moved, re-homed or re-offered. This runs for one population only: a person with no
   * organisation at all, at the moment the old code would have made them one.
   *
   * When the answer is empty — a consumer address, a work domain no organisation has
   * verified, an account we cannot vouch for, or Clerk being unreachable — this falls
   * straight through to the server's workspace decision below. That is the common case and it is
   * the case the product has to keep getting right.
   */
  if (!options.chooseOwnWorkspace && !forPurchase) {
    const { offers, pendingRequests } = await findJoinOffers(clerk, user);
    if (offers.length > 0 || pendingRequests.length > 0) {
      return { ok: true, kind: "choice", offers, pendingRequests };
    }
  }

  /*
   * 🔑 THE WORKSPACE IS THE COMPANY (Maria, 5 Oct 2026). The browser no longer names or
   * creates the organisation. Command's server decides, because only it can (a) read Clerk's
   * verdict on the address without trusting this tab, and (b) serialise two colleagues who
   * sign up in the same second into ONE workspace. See `src/onboarding/workspace-by-domain.ts`.
   *
   * - A verified company address: the server creates "Finflare" (they are its admin) or adds
   *   them to the existing one as a member.
   * - A personal or unverified address: nothing is created until the person tells us their
   *   business name; the caller renders that question.
   */
  const requestWorkspace = options.requestWorkspace ?? requestWorkspaceFromCommand;
  const businessName = options.businessName?.trim() || null;
  let workspace: WorkspaceRequestResult;
  try {
    workspace = await requestWorkspace(
      forPurchase
        ? {
            businessName,
            purpose: "purchase",
            purchaseIntentId: options.purchaseIntentId ?? null,
            chosenOrgId: options.chosenOrgId ?? null,
          }
        : { businessName },
    );
  } catch (err: unknown) {
    return {
      ok: false,
      kind: "error",
      reason: `workspace_request_failed: ${err instanceof Error ? err.message : String(err)}`,
    };
  }
  if (!workspace.ok) {
    return workspace.retryable === false
      ? { ok: false, kind: "error", reason: workspace.error, retryable: false }
      : { ok: false, kind: "error", reason: workspace.error };
  }
  if (workspace.kind === "needs_business_name") {
    return {
      ok: true,
      kind: "needs_business_name",
      reason: workspace.reason,
      rejectedName: businessName,
    };
  }
  if (workspace.kind === "needs_workspace_choice") {
    return { ok: true, kind: "needs_workspace_choice", workspaces: workspace.workspaces };
  }
  if (!isOrganizationId(workspace.orgId)) {
    return { ok: false, kind: "error", reason: "workspace_returned_no_organization" };
  }
  const organizationId = workspace.orgId;
  const organizationName = workspace.orgName;
  const created = workspace.outcome === "created";

  // 🔑 MARK BEFORE CLAIMING, not after failing. The window between "the Clerk org exists"
  // and "Coherence knows about it" is the half-state, and it is open from here. A tab closed
  // inside it leaves no return value to inspect, so the mark is what remembers.
  pendingClaims.mark(organizationId);

  // Activate before claiming so the session token starts carrying the org claim. The claim
  // does not depend on it — it sends X-Org-Context and Atlas verifies membership server
  // side — but everything the page does AFTER the claim does.
  try {
    await clerk.setActive?.({ organization: organizationId });
    await clerk.session?.getToken?.({ template: "command-session", skipCache: true });
  } catch {
    // A failed activation is not fatal to the claim; the org still exists and
    // X-Org-Context carries it. The caller re-reads memberships either way.
  }

  /*
   * 🔴 A PURCHASE NAMES ITS OWN INTENT (7 Oct 2026). With no intent, Atlas's claim falls back
   * to the intent in the account's Clerk metadata, and after a second payment that is the
   * OTHER one: this claim would attach it, then the page's claim the first, two paid intents on
   * one org. Naming it makes this the same idempotent claim the page makes next.
   */
  const purchaseIntentId = forPurchase ? options.purchaseIntentId?.trim() || null : null;
  const claimed = await claim(
    purchaseIntentId
      ? { orgId: organizationId, intentId: purchaseIntentId }
      : { orgId: organizationId },
  );
  if (!claimed.ok) {
    // See the failure-direction note in the file header: a Clerk org with no tenancy
    // behind it is the half-state, not a partial success. The mark stays, so the next
    // load retries this claim rather than assuming the org is fine because it exists.
    return { ok: false, kind: "error", reason: `provisioning_failed: ${claimed.error}` };
  }
  pendingClaims.clear(organizationId);

  return {
    ok: true,
    kind: "organization",
    organization: { orgId: organizationId, orgName: organizationName },
    created,
    role: workspace.role,
  };
}
